import { useState, useCallback } from "react";
import Modal from "components/BaseWidgets/modal";
import Cropper from "react-easy-crop";
import "./style.scss";
import Button from "components/BaseWidgets/button";
import DraggerComponent from "components/CustomWidgets/dragAndDrop";
import { useIdVerification } from "hooks/api/useAccountVerify";
import { useQueryClient } from "@tanstack/react-query";
import AlertNotification from "components/CustomWidgets/alertNotification";
import { getCroppedImg } from "utils/cropImage";

function DocumentModal({ open, setOpen, onOk, aspectRatio = 1.6 }) {
  const [fileList, setFileList] = useState([]);
  const [croppedFileList, setCroppedFileList] = useState([]);
  const queryClient = useQueryClient();
  const [status, setStatus] = useState(null);

  // Crop modal states
  const [cropModalVisible, setCropModalVisible] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imagesToCrop, setImagesToCrop] = useState([]);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const { mutate: requestIdVerification, isLoading } = useIdVerification();

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileListChange = (newFileList) => {
    // Filter only newly added images that need cropping
    const newImages = newFileList.filter(
      (file) =>
        !file.cropped && file.originFileObj && file.type?.startsWith("image/"),
    );

    if (newImages.length > 0) {
      // Open crop modal with new images
      setImagesToCrop(newImages);
      setCurrentImageIndex(0);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCropModalVisible(true);
    } else {
      // Non-image files or already cropped, add directly
      setFileList(newFileList);
    }
  };

  const handleCropCurrent = async () => {
    try {
      const currentImage = imagesToCrop[currentImageIndex];

      if (!currentImage || !croppedAreaPixels) {
        throw new Error("No image or crop area selected");
      }

      const croppedImage = await getCroppedImg(
        URL.createObjectURL(currentImage.originFileObj),
        croppedAreaPixels,
      );

      const newFile = new File([croppedImage], currentImage.name, {
        type: "image/jpeg",
        lastModified: Date.now(),
      });

      const croppedFileData = {
        uid: `cropped-${Date.now()}-${currentImageIndex}`,
        name: newFile.name,
        status: "done",
        cropped: true,
        url: URL.createObjectURL(newFile),
        originFileObj: newFile,
      };

      // Add to cropped list
      setCroppedFileList((prev) => [...prev, croppedFileData]);

      // Move to next image or close modal
      if (currentImageIndex < imagesToCrop.length - 1) {
        setCurrentImageIndex(currentImageIndex + 1);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCroppedAreaPixels(null);
        AlertNotification(
          "success",
          `Image ${currentImageIndex + 1} cropped successfully`,
        );
      } else {
        // All images cropped
        const allCroppedFiles = [...croppedFileList, croppedFileData];
        setFileList((prev) => [...prev, ...allCroppedFiles]);
        setCropModalVisible(false);
        setImagesToCrop([]);
        setCroppedFileList([]);
        setCurrentImageIndex(0);
        AlertNotification("success", `All images cropped successfully`);
      }
    } catch (e) {
      AlertNotification("error", `Failed to crop image: ${e.message}`);
    }
  };

  const handleSkipCurrent = () => {
    if (currentImageIndex < imagesToCrop.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
    } else {
      // Finish cropping session
      if (croppedFileList.length > 0) {
        setFileList((prev) => [...prev, ...croppedFileList]);
      }
      setCropModalVisible(false);
      setImagesToCrop([]);
      setCroppedFileList([]);
      setCurrentImageIndex(0);
    }
  };

  const handleCancelCrop = () => {
    setCropModalVisible(false);
    setImagesToCrop([]);
    setCroppedFileList([]);
    setCurrentImageIndex(0);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const onCancel = () => {
    setOpen(false);
    setFileList([]);
    setCroppedFileList([]);
  };

  const onSubmit = () => {
    if (fileList.length === 0) {
      AlertNotification(
        "warning",
        "Please upload a document before submitting",
      );
      return;
    }

    const formData = new FormData();
    fileList.forEach((file, index) => {
      formData.append("file", file.originFileObj);
    });

    requestIdVerification(formData, {
      onSuccess: (data) => {
        setStatus("success");
        setOpen(false);
        setFileList([]);
        queryClient.invalidateQueries(["verificationDetails"]);
        if (onOk) onOk();
      },
    });
  };

  const modalTitle = (
    <div className="flex flex-col gap-1">
      <span className="font-title-lg fw-700 leading-3">Document upload</span>
      <p className="font-body-md2 leading-5">
        Upload a government issued document to verify your profile
      </p>
    </div>
  );

  const modalFooter = (
    <div className="flex justify-end gap-3 items-center">
      <Button variant="secondary" height={44} onClick={onCancel}>
        Cancel
      </Button>
      <Button
        height={44}
        onClick={onSubmit}
        loading={isLoading}
        isDisabled={fileList.length === 0 || isLoading}
      >
        Submit
      </Button>
    </div>
  );

  const cropModalFooter = (
    <div className="flex justify-between items-center w-full gap-2 flex-wrap">
      <div className="font-body-md2 mt-1">
        Image {currentImageIndex + 1} of {imagesToCrop.length}
      </div>
      <div className="flex gap-3 flex-wrap">
        <Button variant="secondary" height={44} onClick={handleCancelCrop}>
          Cancel All
        </Button>
        <Button variant="secondary" height={44} onClick={handleSkipCurrent}>
          Skip
        </Button>
        <Button height={44} onClick={handleCropCurrent}>
          {currentImageIndex < imagesToCrop.length - 1
            ? "Crop & Next"
            : "Crop & Finish"}
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <Modal
        wrapClassName="document-upload-modal"
        isOpen={open}
        onCancel={onCancel}
        title={modalTitle}
        closable
        width={600}
        footer={modalFooter}
      >
        <DraggerComponent
          fileList={fileList}
          setFileList={handleFileListChange}
          multiple={true}
        />
      </Modal>

      {/* Crop Modal */}
      <Modal
        title="Crop Images"
        isOpen={cropModalVisible}
        onCancel={handleCancelCrop}
        closable
        footer={cropModalFooter}
        wrapClassName="crop-modal-wrapper"
      >
        <div className="crop-container">
          {imagesToCrop[currentImageIndex] && (
            <Cropper
              image={URL.createObjectURL(
                imagesToCrop[currentImageIndex].originFileObj,
              )}
              crop={crop}
              zoom={zoom}
              aspect={aspectRatio}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
              cropShape="rect"
              showGrid={true}
              style={{
                containerStyle: {
                  height: "400px",
                  width: "100%",
                  position: "relative",
                },
              }}
            />
          )}
        </div>
      </Modal>
    </>
  );
}

export default DocumentModal;
